import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import {
  chileDateToDateTimeLocal,
  dateTimeLocalToChileDate,
} from '@/lib/timezone';
import type { ArcadeChallengeRow } from '@/types';

// The shape both challenge dialogs hold. Every value is a string so the inputs stay
// controlled; `toPayload` turns it into what the controller permits — which is the same
// conversion `dateTimeLocalToChileDate` did inside the server actions.
export type ArcadeChallengeFormData = {
  eventId: string;
  name: string;
  slug: string;
  submissionDeadline: string;
  votingDeadline: string;
};

const DATE_FIELDS = ['submissionDeadline', 'votingDeadline'] as const;

export function emptyChallengeForm(
  eventId: string,
): ArcadeChallengeFormData {
  return {
    eventId,
    name: '',
    slug: '',
    submissionDeadline: '',
    votingDeadline: '',
  };
}

export function challengeToForm(
  challenge: ArcadeChallengeRow,
): ArcadeChallengeFormData {
  return {
    eventId: challenge.eventId,
    name: challenge.name,
    slug: challenge.slug,
    submissionDeadline: chileDateToDateTimeLocal(
      new Date(challenge.submissionDeadline),
    ),
    votingDeadline: chileDateToDateTimeLocal(
      new Date(challenge.votingDeadline),
    ),
  };
}

export function toPayload(
  data: ArcadeChallengeFormData,
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...data };

  for (const field of DATE_FIELDS) {
    payload[field] =
      dateTimeLocalToChileDate(data[field] || undefined)?.toISOString() ?? null;
  }

  return payload;
}

// The Next dialogs used shadcn's react-hook-form wrappers, which are deliberately not
// ported (U2). This reproduces the markup FormItem/FormLabel/FormMessage rendered.
export function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label?: string;
  htmlFor?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-2">
      {label && (
        <Label htmlFor={htmlFor} data-error={error ? 'true' : undefined}>
          {label}
        </Label>
      )}
      {children}
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}
